import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../../dark-hero/colors";
import DarkHeroPageEffects from "../../dark-hero/DarkHeroPageEffects";
import { PageContainer } from "../../dark-hero/PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";

const heroSectionSx = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: HERO_BG,
  px: { xs: 2, md: 4 },
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  pb: { xs: "80px", md: "96px" },
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const REVEAL_TRANSITION = { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const sectionSx = {
  py: { xs: "80px", md: "120px" },
  px: { xs: 2, md: 4 },
  borderTop: `1px solid ${colors.border.subtle}`,
  backgroundColor: HERO_BG,
};

const cardSx = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
  border: "1px solid rgba(255,255,255,0.04)",
  borderRadius: "18px",
};

const titleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 2,
};

const descSx = {
  maxWidth: "34rem",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  color: colors.text.secondary,
  mb: { xs: 6, md: 8 },
};

const btnPrimarySx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.contrast,
  background: colors.primary.main,
  px: "32px",
  py: "14px",
  textDecoration: "none",
  clipPath:
    "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
  border: "1px solid transparent",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease, border-color .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.12),
        transparent
      )
    `,
    transform: "translateX(-120%)",
    transition: "transform .6s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,255,255,0.18)",
    boxShadow:
      "0 10px 30px rgba(232,163,61,0.18)",
  },
  "&:hover::before": {
    transform: "translateX(120%)",
  },
  "&:active": {
    transform: "translateY(0px)",
  },
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background:
      "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background:
      "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow:
      "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": {
    opacity: 1,
  },
  "&:active": {
    transform: "translateY(0px)",
  },
};

const GOLD_GRADIENT = "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)";

const ONBOARDING_STEPS = [
  {
    num: "01",
    tag: "Step 01",
    title: "Join with Steam",
    body: "Sign in with your Steam account. That is all you need to join. Ouro never touches your inventory, trades, or market access.",
    note: "Read-only Steam access only. No inventory, trade, or market permissions granted.",
    icon: (
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
  },
  {
    num: "02",
    tag: "Step 02",
    title: "Add context",
    body: "Region, preferences, and FaceIT history are optional. They build your reputation and a better starting rating. An ID check through didit.me is also optional, and it is not required to play.",
    note: "None of this blocks signup. Steam is enough.",
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    num: "03",
    tag: "Step 03",
    title: "Coordinate your queue",
    body: "While the founding player base grows, regional queues are coordinated around evening windows — roughly 6–10pm local time. The more players that join your region, the faster those windows expand.",
    note: "Windows widen as regional density improves. Early players have real influence here.",
    icon: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </>
    ),
  },
];

const UNLOCKS = [
  { num: "01", title: "Competitive context", body: "Ouro imports relevant history — FaceIT data and competitive background — so your starting point reflects who you actually are, not a blank slate." },
  { num: "02", title: "Reputation profile", body: "You start building a long-term record across six dimensions. Unlike most platforms, your history here is permanent and tied to you — not discarded after each session." },
  { num: "03", title: "Membership controls", body: "Higher membership tiers unlock increasingly fine control over map preferences, server locations, and queue behavior. You decide how specific you want to be." },
  { num: "04", title: "Accountable reporting", body: "Reports become part of a real accountability loop — reviewed by admins, with outcomes that affect both the reporter and the reported player. Not a throwaway button." },
  { num: "05", title: "Impact rating", body: "Every match contributes to your Impact Rating — a single number that reflects what you actually did in-game. Opening duels, trades, utility, playing for the round. Not just kills." },
  { num: "06", title: "Opt-in queue preferences", body: "Women players can opt into a soft preference for playing with more women — opt-in only, never a hard filter. It doesn't change anything for anyone who doesn't use it." },
];

const IMPACT_PILLARS = [
  { label: "Duels", body: "Opening picks and trade success rate" },
  { label: "Utility", body: "Flash assists, smokes, effective nades" },
  { label: "Round play", body: "Clutches, bomb plants, defuses" },
  { label: "Contribution", body: "Playing for the team, not the stat line" },
];

const QUEUE_TIMES = [
  { region: "Europe", window: "18:00 – 22:00", badge: "Active", active: true },
  { region: "North America", window: "18:00 – 22:00", badge: "Active", active: true },
  { region: "South America", window: "18:00 – 22:00", badge: "Building", active: false },
  { region: "Asia Pacific", window: "18:00 – 22:00", badge: "Building", active: false },
];

const DENSITY_STAGES = [
  { label: "Founding stage", window: "4hr window", width: "25%", note: "Coordinated evening slots. You're here now." },
  { label: "Growing density", window: "8hr window", width: "55%", note: "Afternoon through late evening." },
  { label: "Healthy region", window: "Always-on", width: "100%", note: "Full 24hr coverage, no window coordination needed.", highlight: true },
];

const FOR_YES = [
  "You're tired of queuing with players who abandon at the first sign of a deficit.",
  "You want your reputation to mean something across matches, not reset every session.",
  "You'd rather have fine-grained control over your queue than accept whatever matchmaking decides.",
  "You believe reporting should actually do something — and be accountable both ways.",
  "You're willing to play during coordinated windows while the platform builds density in your region.",
  "You want to be part of building something, not just consuming it.",
];

const FOR_NO = [
  "You need on-demand queues at any hour — regional density isn't there yet.",
  "You want a fully polished product — Ouro is early and being built openly.",
  "You're looking for a plug-and-play replacement with no onboarding friction.",
];

function Eyebrow({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
      <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
      <Typography
        sx={{
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: colors.primary.hover,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionIntro({ label, title, description }) {
  return (
    <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
      <Eyebrow>{label}</Eyebrow>
      <Typography component="h2" sx={titleSx}>{title}</Typography>
      {description ? <Typography sx={descSx}>{description}</Typography> : null}
    </MotionBox>
  );
}

function StepIcon({ children }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      sx={{ width: 32, height: 32, mb: 2.5, color: colors.primary.main }}
    >
      {children}
    </Box>
  );
}

function DensityBar({ width, delay = 0 }) {
  return (
    <Box sx={{ height: 4, background: colors.background.surface, position: "relative", overflow: "hidden" }}>
      <MotionBox
        initial={{ width: 0 }}
        whileInView={{ width }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.5, delay, ease: [0.22, 1, 0.36, 1] }}
        sx={{ height: "100%", background: colors.primary.main }}
      />
    </Box>
  );
}

export default function HowItWorksPage() {
  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box component="header" sx={heroSectionSx}>
        <DarkHeroPageEffects watermark="HOW IT WORKS" />

        <Box sx={{ position: "relative", zIndex: 2, maxWidth: "75rem", mx: "auto", width: "100%" }}>
          <MotionBox {...HERO_ENTER} transition={REVEAL_TRANSITION}>
            <Eyebrow>How it works</Eyebrow>
          </MotionBox>

          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.075em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: colors.text.primary,
              maxWidth: 900,
              mb: 4,
            }}
          >
            Three moves before
            <br />
            your first{" "}
            <Box component="span" sx={{ background: GOLD_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              match.
            </Box>
          </MotionBox>

          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "32rem",
                mb: 6,
              }}
            >
              Join with Steam. That is all you need. The rest builds your reputation.
              Then help your region build the density it needs for cleaner games.
            </Typography>
          </MotionBox>

          <MotionBox
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.3 }}
            sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join the Founding Community
            </Box>
            <Box component={Link} to="/pricing" sx={btnGhostSx}>
              See pricing →
            </Box>
          </MotionBox>
        </Box>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Getting started"
            title="How you get in."
            description="The onboarding is short and intentional. Every step exists to give the platform a better picture of who you are before you ever queue."
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {ONBOARDING_STEPS.map((step, index) => (
              <MotionBox
                key={step.num}
                {...REVEAL}
                transition={{ ...REVEAL_TRANSITION, delay: index * 0.08 }}
                sx={{
                  ...cardSx,
                  p: { xs: "32px 24px", md: "40px 36px" },
                  position: "relative",
                  overflow: "hidden",
                  transition: "border-color 0.3s",
                  "&:hover": { borderColor: colors.border.focus },
                  "&:hover .step-num-big": { color: "rgba(255,255,255,0.07)" },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "35%",
                    height: "1px",
                    zIndex: 1,
                    pointerEvents: "none",
                    background: `
                      linear-gradient(
                        90deg,
                        transparent,
                        rgba(246,185,76,0.9),
                        transparent
                      )
                    `,
                    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
                  }}
                />
                <Typography
                  className="step-num-big"
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "4.5rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.04)",
                    lineHeight: 1,
                    position: "absolute",
                    top: 24,
                    right: 28,
                    transition: "color 0.3s",
                  }}
                >
                  {step.num}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: colors.primary.main,
                    mb: 2.5,
                  }}
                >
                  {step.tag}
                </Typography>
                <StepIcon>{step.icon}</StepIcon>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.375rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    mb: 1.75,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.75 }}>
                  {step.body}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: colors.text.muted,
                    lineHeight: 1.6,
                    pt: 2,
                    mt: 2.5,
                    borderTop: `1px solid ${colors.border.subtle}`,
                  }}
                >
                  {step.note}
                </Typography>
              </MotionBox>
            ))}
          </Box>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="After you join"
            title="What opens up."
            description="Steam is enough to be here. The pieces below build your reputation. An ID check through didit.me is optional."
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {UNLOCKS.map((item, index) => (
              <MotionBox
                key={item.num}
                {...REVEAL}
                transition={{ ...REVEAL_TRANSITION, delay: index * 0.06 }}
                sx={{
                  ...cardSx,
                  p: { xs: "28px 24px", md: "32px 28px" },
                  transition: "border-color 0.3s",
                  "&:hover": { borderColor: colors.border.focus },
                }}
              >
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.15em", color: colors.text.muted, mb: 1.75 }}>
                  {item.num} —
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.0625rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    mb: 1.25,
                  }}
                >
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.7 }}>
                  {item.body}
                </Typography>
              </MotionBox>
            ))}
          </Box>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Impact Rating"
            title="One number. What you actually did."
            description="Impact Rating isn't your KDA. It's a single match-level score that measures your real contribution — the plays that win rounds, not just the ones that look good in the scoreboard."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              ...cardSx,
              p: { xs: "40px 32px", md: "56px 64px" },
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
              gap: { xs: 4, md: 6 },
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: `linear-gradient(90deg, ${colors.primary.main} 0%, transparent 70%)`,
              },
            }}
          >
            <Typography
              aria-hidden
              sx={{
                position: "absolute",
                right: -40,
                top: "50%",
                transform: "translateY(-50%)",
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: { xs: "8rem", md: "12.5rem" },
                fontWeight: 700,
                color: "rgba(255,255,255,0.02)",
                lineHeight: 1,
                pointerEvents: "none",
                display: { xs: "none", sm: "block" },
              }}
            >
              IR
            </Typography>
            <Box sx={{ position: "relative" }}>
              <Typography sx={{ fontSize: "0.9375rem", color: colors.text.secondary, lineHeight: 1.8, maxWidth: "34rem", mb: 4 }}>
                Every verified match contributes a score based on the things that actually matter: opening duels taken, trades executed, utility effectiveness, and whether you played for the round or for your own stats. A single number per match, permanently tied to your account.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 2.5,
                }}
              >
                {IMPACT_PILLARS.map((pillar) => (
                  <Box key={pillar.label}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        color: colors.text.muted,
                        mb: 0.75,
                      }}
                    >
                      {pillar.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.5 }}>
                      {pillar.body}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ textAlign: { xs: "left", md: "right" }, position: "relative" }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: { xs: "4rem", md: "5rem" },
                  fontWeight: 700,
                  color: colors.text.primary,
                  lineHeight: 1,
                }}
              >
                IR
              </Typography>
              <Typography sx={{ fontSize: "1.125rem", color: colors.text.muted, letterSpacing: "0.05em", mt: 0.75 }}>
                Per-match score
              </Typography>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Regional queues"
            title="When queues are live."
            description="Early on, Ouro coordinates queue windows so players can actually find each other. As regional density grows, those windows expand — and eventually, open up completely."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 5, md: 8 },
              alignItems: "start",
            }}
          >
            <Box sx={{ ...cardSx, p: 5 }}>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                  mb: 0.5,
                }}
              >
                Current windows
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.6, mb: 3 }}>
                Evening-focused while founding density builds.
              </Typography>
              {QUEUE_TIMES.map((row, index) => (
                <Box
                  key={row.region}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    py: "14px",
                    borderBottom: index === QUEUE_TIMES.length - 1 ? "none" : `1px solid ${colors.border.subtle}`,
                  }}
                >
                  <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary }}>{row.region}</Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      color: colors.text.primary,
                    }}
                  >
                    {row.window}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      px: 1,
                      py: 0.375,
                      color: colors.primary.main,
                      background: "rgba(232,163,61,0.1)",
                      border: "1px solid rgba(232,163,61,0.2)",
                      flexShrink: 0,
                    }}
                  >
                    {row.badge}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                  mb: 3,
                }}
              >
                How density expands coverage
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
                {DENSITY_STAGES.map((stage, index) => (
                  <Box key={stage.label}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.25 }}>
                      <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary }}>{stage.label}</Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', system-ui, sans-serif",
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          color: stage.highlight ? colors.primary.main : colors.text.muted,
                        }}
                      >
                        {stage.window}
                      </Typography>
                    </Box>
                    <DensityBar width={stage.width} delay={index * 0.15} />
                    <Typography sx={{ fontSize: "0.75rem", color: colors.text.muted, mt: 0.75 }}>
                      {stage.note}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Who it's for"
            title="Built for players who give a damn."
            description="Ouro isn't hiding that it's early. It's for the players who want cleaner games badly enough to help build them."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            <Box sx={{ ...cardSx, p: { xs: "36px 28px", md: "48px 44px" } }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: colors.text.primary,
                  mb: 2.5,
                }}
              >
                This is for you if —
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {FOR_YES.map((item) => (
                  <Typography
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      fontSize: "0.875rem",
                      color: colors.text.secondary,
                      lineHeight: 1.6,
                      "&::before": {
                        content: '""',
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        flexShrink: 0,
                        mt: "7px",
                        background: colors.primary.main,
                      },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Box sx={{ ...cardSx, p: { xs: "36px 28px", md: "48px 44px" } }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: colors.text.muted,
                  mb: 2.5,
                }}
              >
                Maybe not yet if —
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {FOR_NO.map((item) => (
                  <Typography
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      fontSize: "0.875rem",
                      color: colors.text.muted,
                      lineHeight: 1.6,
                      "&::before": {
                        content: '""',
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        flexShrink: 0,
                        mt: "7px",
                        background: colors.text.muted,
                      },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: colors.text.muted,
                  lineHeight: 1.7,
                  mt: 4,
                  pt: 3,
                  borderTop: `1px solid ${colors.border.subtle}`,
                }}
              >
                Ouro is not for everyone right now — and that&apos;s the point. Every founding player who joins raises the floor for everyone who comes after them.
              </Typography>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer
          sx={{
            maxWidth: "75rem",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            alignItems: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
            <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>
              Ready to queue
              <br />
              with better people?
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: colors.text.secondary, maxWidth: 400, lineHeight: 1.7 }}>
              Join the founding community. Your region needs density — and your record starts the moment you do.
            </Typography>
          </MotionBox>
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join founding community
            </Box>
            <Box component={Link} to="/pricing" sx={btnGhostSx}>
              See pricing →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>
    </Box>
  );
}
